import * as THREE from 'three';
import { G, K_BEV, hemi, mesh, rim, rim2, scene, stdMat, sun } from './scene';
import { edgeRing, floorMesh, gridCoarse, gridFine, terrainGroup } from '../world/arena';
import { hangarProps } from './hangar-props';
/* ============================================================
   機庫場景（3D）
   ============================================================

   在此之前機庫是「機體站在戰場的地板上、背景純黑」—— 標題寫著機庫，
   但畫面上沒有任何一樣東西是機庫。這個檔給它一個實際的空間。

   **空間的三個作用**（不是裝飾）：

   1. **尺度參照**。旁邊沒有東西的時候，機體是大是小完全讀不出來。
      格架、貨櫃、警戒帶都是人類尺度的物件，機體靠它們才顯得有 4 公尺高。
   2. **接地**。站台是實心平台且接收陰影，機體有影子落在上面才站得住。
      原本的影子落在無限延伸的灰平面上，讀起來像貼圖。
   3. **暗部**。緣光要有效，背後必須有暗的東西擋住（v2 §1.3b）。
      純黑背景反而讓輪廓光沒有對比對象。

   **全部是靜態的**，一次建立、切 `visible` 開關，不進模擬層。 */

export var hangarGroup = new THREE.Group();
hangarGroup.visible = false;
scene.add(hangarGroup);

/* 五種材質共用給全部構件 —— 與機體同一套規則（實作文件 v2 §1.4）。
   機庫的材質刻意比機體**粗糙且低對比**：主角是機體，環境不能搶反光。 */
export var MH = {
  floor: stdMat(0x070c13, { metal: 0.12, rough: 0.95 }),
  deck: stdMat(0x151f2e, { metal: 0.55, rough: 0.5, flat: true }),
  wall: stdMat(0x0d141e, { metal: 0.3, rough: 0.82, flat: true }),
  rib: stdMat(0x1c2839, { metal: 0.62, rough: 0.4, flat: true }),
  dark: stdMat(0x070b11, { metal: 0.25, rough: 0.85, flat: true }),
  glow: stdMat(0x0a0e16, { em: 0x5fe3ff, ei: 1.35, flat: true }),
  warn: stdMat(0x0d1019, { em: 0xd9a441, ei: 0.75, flat: true }),
  lamp: new THREE.MeshBasicMaterial({ color: 0x9fbcd8 })
};

var DECK_R = 3.6;          // 站台半徑
var DECK_H = 0.55;         // 站台高度（頂面固定在 y = 0，機體腳底就踩在這裡）
var FLOOR_Y = -DECK_H;
var WALL_Z = -12, WALL_H = 9.5, ROOM_W = 30;

function add(o) { hangarGroup.add(o); return o; }

/* ---------- 地面與站台 ---------- */
function buildFloor() {
  var f = mesh(G.box, MH.floor, [ROOM_W + 14, 0.4, 34], [0, FLOOR_Y - 0.2, -4]);
  f.receiveShadow = true; add(f);

  /* 格柵：GridHelper 是 LineSegments，整片只要 1 個 draw call。
     兩層疏密營造「地板有分區」，比單一格線更像工業場地。 */
  var g1 = new THREE.GridHelper(34, 68, 0x16202e, 0x121a26);
  g1.position.set(0, FLOOR_Y + 0.005, -4); add(g1);
  var g2 = new THREE.GridHelper(34, 17, 0x243448, 0x1d2938);
  g2.position.set(0, FLOOR_Y + 0.01, -4); add(g2);

  /* 站台。八角柱（`G.bev` 是 8 段 + thetaStart π/8）讓四個平面對齊軸向，
     正面看是一條直的邊而不是圓弧 —— 圓盤太像模型檢視器的轉盤。 */
  var base = mesh(G.bev, MH.deck, [DECK_R * 2 * K_BEV, DECK_H, DECK_R * 2 * K_BEV], [0, -DECK_H / 2, 0]);
  base.receiveShadow = true; add(base);
  // 下緣內縮一圈，站台才讀得出是浮起來的厚板而不是一坨實心
  add(mesh(G.bev, MH.dark, [(DECK_R - 0.28) * 2 * K_BEV, 0.16, (DECK_R - 0.28) * 2 * K_BEV], [0, -DECK_H - 0.06, 0]));

  /* 站台面的發光溝槽：一圈環 + 四條放射線。
     它同時是「機體站的位置」的指示 —— 玩家一眼知道畫面的主體在哪。 */
  var ring = new THREE.Mesh(new THREE.RingGeometry(DECK_R - 0.42, DECK_R - 0.28, 8, 1, Math.PI / 8),
    new THREE.MeshBasicMaterial({ color: 0x2f7fa8, transparent: true, opacity: 0.48, side: THREE.DoubleSide }));
  ring.rotation.x = -Math.PI / 2; ring.position.y = 0.012; add(ring);
  for (var i = 0; i < 4; i++) {
    var a = i * Math.PI / 2 + Math.PI / 4;
    add(mesh(G.box, MH.glow, [0.09, 0.02, 1.5],
      [Math.sin(a) * (DECK_R - 1.05), 0.014, Math.cos(a) * (DECK_R - 1.05)], [0, a, 0]));
  }

  /* 警戒帶：站台外圍的斜紋。人類尺度的標記，是判讀機體大小的第二個參照。 */
  for (var k = 0; k < 26; k++) {
    var t = (k / 26) * Math.PI * 2;
    add(mesh(G.box, k % 2 ? MH.warn : MH.dark, [0.34, 0.03, 0.62],
      [Math.sin(t) * (DECK_R + 0.55), FLOOR_Y + 0.02, Math.cos(t) * (DECK_R + 0.55)], [0, t, 0]));
  }
}

/* ---------- 牆與門 ---------- */
function buildWalls() {
  // 背牆本體。分三片、中間那片退後，讓正中央有一個凹進去的深度
  add(mesh(G.box, MH.wall, [ROOM_W, WALL_H, 0.8], [0, WALL_H / 2 + FLOOR_Y, WALL_Z]));
  add(mesh(G.box, MH.dark, [9.5, WALL_H - 0.6, 0.8], [0, WALL_H / 2 + FLOOR_Y, WALL_Z + 0.5]));

  /* 縱向肋。每 2.4 一根，讓 30 單位寬的牆有節奏 ——
     沒有肋的大平面在任何角度都讀成一片灰，等於沒做。 */
  for (var x = -13.2; x <= 13.2; x += 2.4) {
    if (Math.abs(x) < 4.6) continue;                       // 中央讓給門
    add(mesh(G.box, MH.rib, [0.34, WALL_H - 1.2, 0.42], [x, WALL_H / 2 + FLOOR_Y - 0.3, WALL_Z + 0.55]));
  }

  /* 中央的通道口：深處有光。這是整個場景唯一的「外面」——
     沒有它的話空間讀起來是密閉的盒子，缺少縱深。 */
  add(mesh(G.box, MH.dark, [7.4, 6.2, 0.5], [0, 3.1 + FLOOR_Y, WALL_Z + 0.9]));
  add(mesh(G.box, MH.glow, [7.8, 0.14, 0.3], [0, 6.3 + FLOOR_Y, WALL_Z + 1.1]));
  add(mesh(G.box, MH.glow, [0.14, 6.3, 0.3], [-3.85, 3.15 + FLOOR_Y, WALL_Z + 1.1]));
  add(mesh(G.box, MH.glow, [0.14, 6.3, 0.3], [3.85, 3.15 + FLOOR_Y, WALL_Z + 1.1]));
  // 通道深處的地板亮條，讀起來是「還有路」
  add(mesh(G.box, MH.warn, [5.4, 0.04, 0.5], [0, FLOOR_Y + 0.03, WALL_Z + 1.4]));

  // 兩側牆：只做剪影與遮擋，細節看不到所以不放
  [-1, 1].forEach(function (s) {
    add(mesh(G.box, MH.wall, [0.8, WALL_H, 22], [s * (ROOM_W / 2 - 0.4), WALL_H / 2 + FLOOR_Y, -3]));
    for (var z = -11; z <= 5; z += 3.2) {
      add(mesh(G.box, MH.rib, [0.4, WALL_H - 2, 0.36], [s * (ROOM_W / 2 - 0.85), WALL_H / 2 + FLOOR_Y - 0.6, z]));
    }
  });
}

/* ---------- 燈桁 ---------- */
function buildLights() {
  /* 頭頂兩排燈。**燈具本身要看得到**：畫面上有光源的實體，
     打光才會讀成「這個空間裡的光」而不是「後製加的」。

     位置刻意退到機體後方（z ≤ -1）且拉高到 9.4 —— 第一版擺在 7.6、
     一路延伸到 z = +5，於是從機庫的仰角看過去，一條白色燈管
     正好橫切過機體的頭。天花板的東西不該進入主體的視野高度。 */
  [-4.6, 4.6].forEach(function (x) {
    add(mesh(G.box, MH.dark, [1.5, 0.5, 9], [x, 9.4 + FLOOR_Y, -7]));           // 燈罩
    add(mesh(G.box, MH.lamp, [1.05, 0.09, 7.8], [x, 9.14 + FLOOR_Y, -7]));      // 燈管
    for (var z = -11; z <= -1; z += 3.3) {                                        // 吊桿
      add(mesh(G.cyl, MH.rib, [0.12, 1.2, 0.12], [x, 10.2 + FLOOR_Y, z]));
    }
  });
  // 橫向桁架，把兩排燈連起來 —— 天花板不能是空的
  for (var z2 = -11; z2 <= -1; z2 += 3.3) {
    add(mesh(G.box, MH.rib, [10.6, 0.22, 0.3], [0, 10.7 + FLOOR_Y, z2]));
  }
  // 站台兩側的低位補光燈，把腿部從暗部拉出來
  [-1, 1].forEach(function (s) {
    add(mesh(G.box, MH.dark, [0.7, 0.7, 2.6], [s * 5.6, FLOOR_Y + 0.35, 0.6]));
    add(mesh(G.box, MH.lamp, [0.1, 0.42, 2.2], [s * (5.6 - 0.36), FLOOR_Y + 0.4, 0.6]));
  });
}

var built = false;
function build() {
  if (built) return; built = true;
  buildFloor(); buildWalls(); buildLights();
  hangarProps(add);
}

/* ---------- 進出 ----------
   機庫與戰場共用同一個 scene 與同一組燈，所以進出要成對地換掉：
   **戰場的地板、格線、地形要藏起來**，否則機庫的地板下面會透出
   一片延伸到 68 單位外的戰場圓盤，空間感當場破功。 */
var saved: any = null;

export function enterHangarScene() {
  build();
  saved = {
    bg: scene.background, fog: scene.fog,
    hemi: hemi.intensity, sunI: sun.intensity, sunC: sun.color.getHex(),
    rimI: rim.intensity, rimC: rim.color.getHex(),
    rim2I: rim2.intensity, rim2C: rim2.color.getHex()
  };
  floorMesh.visible = false; gridFine.visible = false;
  gridCoarse.visible = false; edgeRing.visible = false; terrainGroup.visible = false;
  hangarGroup.visible = true;

  scene.background = new THREE.Color(0x05080e);
  scene.fog = new THREE.Fog(0x05080e, 20, 52);

  /* 打光。**第一版把這組比例調錯了**，值得寫下來：

     rim 是「緣光」，但 `DirectionalLight` 沒有距離衰減也沒有角度限制 ——
     它照亮所有法線朝向它的面，包括正面。戰場的俯視鏡頭下看不出來，
     機庫的近距離水平視角下它就變成主光：暖橘 0.62 + 冷藍 0.55 加起來
     比白色主光還強，整個畫面（連機體）被染成米白偏棕。

     判準：**主光要比兩盞 rim 的總和大**。緣光只負責描邊，不負責照明。

     第二件事：**主光也不能太亮。** ACES 的高光肩部會往橘黃偏，
     sun 開到 1.15 時裝甲的受光面被推進那一段，整片變米色 ——
     看起來像「打了暖光」，其實是曝過頭。0.95 是實測不偏色的上限。 */
  hemi.intensity = 0.30;               // 戰場是 0.72。暗部夠暗，縫線與分層才讀得到
  sun.intensity = 0.95; sun.color.setHex(0xeaf3ff);
  rim.color.setHex(0xff8a4a); rim.intensity = 0.30;
  rim2.color.setHex(0x6fa8ff); rim2.intensity = 0.24;
  hangarSun();
}

/* 機庫的主光方向。戰場的 `sunFollow()` 每幀把太陽挪到玩家頭上，
   機庫不需要跟隨，而且要一個**為了展示而挑的角度**：
   左前上方 45°，機體的右半受光、左半留給暖 rim，影子往右後落在站台上。
   距離維持 58 —— 陰影相機的 near/far（42/74）是照這個距離配的。 */
var SUN_H = new THREE.Vector3(-14, 30, 20).normalize().multiplyScalar(58);
export function hangarSun() {
  sun.position.copy(SUN_H);
  sun.target.position.set(0, 1.0, 0);
  sun.target.updateMatrixWorld();
}

export function exitHangarScene() {
  hangarGroup.visible = false;
  floorMesh.visible = true; gridFine.visible = true;
  gridCoarse.visible = true; edgeRing.visible = true; terrainGroup.visible = true;
  if (!saved) return;
  scene.background = saved.bg; scene.fog = saved.fog;
  hemi.intensity = saved.hemi;
  sun.intensity = saved.sunI; sun.color.setHex(saved.sunC);
  rim.intensity = saved.rimI; rim.color.setHex(saved.rimC);
  rim2.intensity = saved.rim2I; rim2.color.setHex(saved.rim2C);
  saved = null;
}
